﻿import React, { useState, useEffect } from 'react';
import { GetListOfYears, OutputFileReturnName } from 'api/software.js';
import { useQuery } from '@tanstack/react-query';
import { StrikeThroughHeader } from 'components/Headers/strikethrough-header/strikethrough-header';
import LoadingIndicator from 'components/Global/loading-indicator/loading-indicator';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import { TextField, Select, MenuItem, FormControl } from '@mui/material';
import './AnnualStatements.scss';
import { fetchSignedURL } from '../../../../api/file.js';

export default function AnnualStatements() {
  const [yearsList, setYearsList] = useState(null);
  const [filePath, setFilePath] = useState('');
  const [policyId, setPolicyId] = useState('');
  const [year, setYear] = useState('');
  const [msg, setMsg] = useState('');
  const yearsQuery = useQuery({
    queryKey: ['yearsQuery', policyId],
    queryFn: () => GetListOfYears(policyId)(),
    enabled: false,
  });
  const outputFileNameQuery = useQuery({
    queryKey: ['outputFileNameQuery', year],
    queryFn: () =>
      OutputFileReturnName({
        policyNumber: policyId,
        statementYear: year,
        overrideStatementType: true,
      })(),
    enabled: false,
  });

  const handleGetYearsClick = () => {
    //01152026006
    yearsQuery.refetch();
    //console.log("yearsQuery fetch called");
  };

  //needs to be configured
  const generateStatementClick = () => {
    outputFileNameQuery.refetch();
  };

  //when data from Get Years call is updated
  useEffect(() => {
    setFilePath('');
    setYear('');
    //console.log(yearsQuery.data);

    if (yearsQuery && yearsQuery.data) {
      const raw = yearsQuery.data;
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (parsed?.error) {
        setMsg(parsed.error);
        setYearsList(null);
      } else if (Array.isArray(parsed)) {
        setYearsList(parsed);
        if (parsed.length > 0) {
          setYear(parsed[parsed.length - 1]);
        }
      } else {
        setYearsList(null);
      }
    }
  }, [yearsQuery.data]);

  /*since yearsQuery.data does not change when two erroneous policy numbers are called
   * (it remains an empty array):
   * yearsQuery.data === "[]"
   *
   * this useEffect will check if data was updated and return an error message for both
   * the first and the subsequent times an empty array is returned from API call*/
  useEffect(() => {
    if (yearsQuery.data === '[]') {
      setMsg(`No statements found for Policy ${policyId}`);
    }
  }, [yearsQuery.dataUpdatedAt]);

  //Disables button & year list when user changed policy #
  useEffect(() => {
    setYearsList(null);
    setMsg('');
  }, [policyId]);

  //Retrieves files name for the year selected
  useEffect(() => {
    setFilePath('');

    //makes sure year variable is not null
    if (year) {
      setMsg('Click Generate Statement for Annual Statement');
      //console.log('outputFileNameQuery fetch called')
    }
  }, [year]);

  //after file path is retrieved from API call
  useEffect(() => {
    if (outputFileNameQuery != null && outputFileNameQuery.data != null) {
      const raw = outputFileNameQuery.data;
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (parsed?.error) {
        setMsg(parsed.error);
      } else {
        setMsg('Annual Statement File Generated');
        fetchSignedURL('annstmt/' + parsed.stringValue);
      }
    }
  }, [outputFileNameQuery.data]);

  return (
    <div id="annual-statements">
      <StrikeThroughHeader>Annual Statements</StrikeThroughHeader>

      <TextField
        id="policy-number"
        label="Enter Policy Number"
        variant="outlined"
        value={policyId}
        onChange={(e) => setPolicyId(e.target.value)}
        style={{ marginBottom: '20px' }}
      />

      <button
        type="button"
        style={{ marginRight: '40px', marginLeft: '14px' }}
        className="btn btn-primary"
        onClick={() => handleGetYearsClick()}
      >
        Get Years
      </button>

      <FormControl
        sx={{ marginRight: 1, minWidth: 140 }}
        variant="outlined"
        disabled={
          !yearsList || yearsList.length === 0 || outputFileNameQuery.isFetching ? true : false
        }
      >
        <InputLabel id="input-label">Select Year</InputLabel>

        <Select
          variant="outlined"
          input={<OutlinedInput label="Select Year" />}
          value={year ? year : ''}
          onChange={(e) => setYear(e.target.value)}
        >
          {yearsList &&
            yearsList.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      <button
        type="button"
        className="btn btn-primary"
        onClick={() => generateStatementClick()}
        disabled={!year}
      >
        Generate Statement
      </button>

      <p>{msg}</p>

      <div
        style={{
          visibility:
            yearsQuery.isFetching || outputFileNameQuery.isFetching ? 'visible' : 'hidden',
        }}
      >
        <LoadingIndicator />
      </div>
    </div>
  );
}
